import { observer } from 'mobx-react-lite';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { FormSelect } from 'src/shared/components/form-components';

import type { RegularComboBox } from '../../../../shared/entities/control-entities';
import type { TOption } from '../../types';
import { ComboboxDropdown } from '../combobox-dropdown/combobox-dropdown';

type Props = {
  className?: string;
  label?: string;
  item: RegularComboBox;
  isDisabled: boolean;
  onBlur?: VoidFunction;
  onChange(value: unknown): void;
};

export const NewWellRegularComboBox = observer(function NewWellRegularComboBox({
  className,
  label,
  item,
  isDisabled,
  onBlur,
  onChange,
}: Props) {
  const [isOpened, setIsOpened] = useState(false);
  const { init } = item;

  const renderDropdown = (origin: ReactElement): ReactElement => {
    return <ComboboxDropdown fieldId={item.fieldId}>{origin}</ComboboxDropdown>;
  };

  const handleChange = (value: string | number | TOption | TOption[] | null): void => {
    item.clearError();
    setIsOpened(false);
    onChange(value);
  };

  const handleBlur = () => {
    onBlur?.();
  };

  const onClear = () => {
    item.clearItem();
    handleChange(null);
  };

  const onDropdownVisibleChange = (open: boolean): void => {
    setIsOpened(open);
  };

  useEffect(() => init(onChange), [init, onChange]);

  return (
    <FormSelect<string | number | TOption>
      placeholder={item.placeholder}
      open={isOpened}
      value={item.invalidValue || item.archivedValue || item.value}
      label={label}
      className={className}
      errorText={item.errorText}
      options={item.options}
      isDisabled={isDisabled}
      dropdownStyle={{
        zIndex: 3,
      }}
      dataTestId={`combobox-${item.fieldId}`}
      onDropdownVisibleChange={onDropdownVisibleChange}
      dropdownRender={renderDropdown}
      onBlur={handleBlur}
      onClear={onClear}
      onChange={handleChange}
    />
  );
});
